import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from "axios";
import Cookies from "universal-cookie";

const cookies = new Cookies();

/* ---------------- CONFIG ---------------- */

const FALLBACK_ENDPOINTS = [
  "https://api.negary.com/api",
  "https://api.aforoghinia.ir/api",
  "https://api.risman.app/api"
];

const ENDPOINT_SOURCES = [
  "https://config.risman.app/endpoints.json"
];

const STORAGE_KEY = "working_api_endpoint";
const HEALTH_TIMEOUT = 6000;

/* ---------------- STATE ---------------- */

let endpoints: string[] = [...FALLBACK_ENDPOINTS];
let activeEndpoint = localStorage.getItem(STORAGE_KEY) || endpoints[0];
// let activeEndpoint = endpoints[0];
let instance: AxiosInstance;

let switching = false;
let pendingRequests: (() => void)[] = [];

const listeners = new Set<(state: ClientState) => void>();

export type ClientState = {
  activeEndpoint: string;
  switching: boolean;
};

/* ---------------- STATE EMITTER ---------------- */

export function getClientState(): ClientState {
  return {
    activeEndpoint,
    switching
  };
}

function emit() {
  const state = getClientState();
  listeners.forEach(l => l(state));
}

export function subscribe(listener: (state: ClientState) => void) {
  listeners.add(listener);
  listener(getClientState());

  return () => {
    listeners.delete(listener);
  };
}

/* ---------------- ENDPOINT UTILS ---------------- */

async function fetchEndpointSources(): Promise<string[]> {
  const results: string[] = [];

  await Promise.all(
    ENDPOINT_SOURCES.map(async url => {
      try {
        const res = await axios.get(url, { timeout: 5000 });

        if (Array.isArray(res.data?.endpoints)) {
          results.push(...res.data.endpoints);
        }
      } catch {}
    })
  );

  return results;
}

async function probeEndpoint(url: string) {
    const res = await axios.get(url + "/is-risman-up", {
        timeout: HEALTH_TIMEOUT,
        params: { t: Date.now() },
        headers: { "Cache-Control": "no-cache" }
    });

    console.log(url, res);

    if (res.data?.status !== "OK") {
        throw new Error("Invalid health response");
    }

    return true;
}

async function checkEndpoint(url: string) {
  try {
    const start = performance.now();

    await probeEndpoint(url);

    return {
      url,
      ok: true,
      latency: performance.now() - start
    };
  } catch {
    return { url, ok: false, latency: Infinity };
  }
}

async function raceEndpoints(list: string[]) {
    return new Promise<string | null>(resolve => {
        let settled = false;

        list.forEach(url => {
            probeEndpoint(url)
                .then(() => {
                    if (!settled) {
                        console.log("SUCCEEDED", url)
                        settled = true;
                        resolve(url);
                    }
                })
                .catch((err) => {
                    console.log("FAILED", url, err)
                });
            });

        setTimeout(() => {
            if (!settled) resolve(null);
        }, HEALTH_TIMEOUT);
    });
}

/* ---------------- SWITCHING ---------------- */

async function switchEndpoint() {
  if (switching) return;

  switching = true;
  emit();

  const checks = await Promise.all(endpoints.map(checkEndpoint));

  const working = checks
    .filter(x => x.ok)
    .sort((a, b) => a.latency - b.latency);

  if (working.length > 0) {
    activeEndpoint = working[0].url;

    instance.defaults.baseURL = activeEndpoint;

    localStorage.setItem(STORAGE_KEY, activeEndpoint);
  }

  switching = false;
  emit();

  pendingRequests.forEach(r => r());
  pendingRequests = [];
}

function waitForSwitch() {
  return new Promise<void>(resolve => pendingRequests.push(() => resolve()));
}

/* ---------------- INITIALIZATION ---------------- */

async function initialize() {
//   const remoteEndpoints = await fetchEndpointSources();
//   endpoints = Array.from(new Set([...remoteEndpoints, ...FALLBACK_ENDPOINTS]));

  endpoints = Array.from(new Set([...FALLBACK_ENDPOINTS]));

  console.log(activeEndpoint);

  instance = axios.create({
    baseURL: activeEndpoint,
    timeout: 15000
  });

  setupInterceptors();
  emit();

  const fastest = await raceEndpoints(endpoints);
  if (fastest && fastest !== activeEndpoint) {
    activeEndpoint = fastest;
    instance.defaults.baseURL = fastest;
    localStorage.setItem(STORAGE_KEY, fastest);
    emit();
  }
}

/* ---------------- INTERCEPTORS ---------------- */

function setupInterceptors() {
  instance.interceptors.request.use(config => {
    const token =
      cookies.get("currentUser")?.token ||
      cookies.get("adminUser")?.token;

    if (token && !config.headers?.Authorization) {
      config.headers!.Authorization = "Bearer " + token;
    }

    return config;
  });

  instance.interceptors.response.use(
    res => res,

    async (error: AxiosError) => {
      const original = error.config as AxiosRequestConfig & { _retry?: boolean };

      if (error?.response?.status) {
        const status = error.response.status;

        if (status === 401) {
          cookies.remove("currentUser", { path: "/" });
          window.location.href = "/login";
        }

        if (
          status === 403 &&
          !["/wallet","/payment","/challengePayment","/referral"]
            .includes(window.location.pathname)
        ) {
          window.location.href = "/wallet";
        }

        return Promise.reject(error);
      }

      const connectivityError = error.code === "ERR_NETWORK" || error.message === "Network Error" || !error.response;
      if (connectivityError && !original._retry) {
        original._retry = true;
        await switchEndpoint();

        if (switching) await waitForSwitch();

        original.baseURL = activeEndpoint;
        return instance(original);
      }

      return Promise.reject(error);
    }
  );
}

/* ---------------- START ---------------- */

export const ready = initialize();
// initialize();

/* ---------------- EXPORT ---------------- */

export const api = new Proxy({} as AxiosInstance, {
  get(_, key) {
    return async (...args: any[]) => {
      await ready;
      return (instance as any)[key](...args);
    };
  }
});
